import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Auth } from '@angular/fire/auth';
import { ShopSummary } from '../../../../../src/types';
import { FooterComponent } from '../../components/footer/footer.component';
import { NavbarComponent } from '../../components/navbar/navbar.component';
import { FirebaseService } from '../../services/firebase.service';
import { errorKey } from '../../utils/format';

/**
 * /business: the owner's shops. `?add=<address>` (from the home page's "Put it
 * on my shop") fills the form and, once signed in, adds the shop straight away,
 * so a demo turns into a shop with one login and no retyping.
 */
@Component({
   selector: 'app-shops-page',
   standalone: true,
   imports: [NavbarComponent, FooterComponent, TranslateModule, RouterModule, FormsModule],
   templateUrl: './shops-page.component.html',
})
export class ShopsPageComponent implements OnInit {
   signedIn: boolean | null = null;
   shops: ShopSummary[] = [];
   loading = false;
   address = '';
   adding = false;
   errorKey = '';
   private pendingAdd = false;

   constructor(
      private auth: Auth,
      private firebaseService: FirebaseService,
      private route: ActivatedRoute,
      private router: Router,
   ) {}

   ngOnInit() {
      const add = this.route.snapshot.queryParamMap.get('add');
      if (add) {
         this.address = add;
         this.pendingAdd = true;
      }
      this.auth.onAuthStateChanged((user) => {
         this.signedIn = !!user;
         if (!user) return;
         this.load();
         if (this.pendingAdd) {
            this.pendingAdd = false;
            this.add();
         }
      });
   }

   /** Where the login page should send the owner back to. */
   get loginNext(): string {
      return this.address ? `/business?add=${encodeURIComponent(this.address)}` : '/business';
   }

   load() {
      this.loading = true;
      this.firebaseService.getFunction('getMyShops')({}).then((result) => {
         this.shops = (result.data as ShopSummary[]) || [];
      }).catch(() => undefined).finally(() => {
         this.loading = false;
      });
   }

   add() {
      const url = this.address.trim();
      if (!url || this.adding) return;
      this.adding = true;
      this.errorKey = '';
      this.firebaseService.getFunction('addShop')({ url }).then((result) => {
         const shop = result.data as ShopSummary;
         this.router.navigate(['/business', shop.id]);
      }).catch((error) => {
         this.errorKey = errorKey(error);
      }).finally(() => {
         this.adding = false;
      });
   }
}
